import { useCallback, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Budget,
  BudgetItemType,
  BudgetItemTypeEnum,
  CalcOption,
} from "../../../../types/budget/budget";
import {
  useBudgetActions,
  useBudgetMeta,
  useBudgetProvider,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  findTransferCategoryBlockReason,
  hasMoveCategoryDuplicate,
  type TransferCategorySupplierPayload,
} from "../../../../contexts/BudgetContext/reducer-funcion";
import { useProject } from "../../../../contexts/project.context";
import { resolveNewSupplierId } from "../../../../utils/budget-supplier-id";
import type { TransferTarget } from "../TransferCategorySupplierModal";
import { useTransferTargets } from "./useTransferTargets";

/**
 * Ações de uma CATEGORIA de um fornecedor: excluir, mover de forma de cobrança
 * e transferir para outro fornecedor.
 *
 * Extraído do `BudgetSupplier` porque a visão "Por Agrupamento" precisa das
 * mesmas três — e são operações com regra própria (id do destino, duplicidade,
 * autosave com contexto de auditoria) que não podem divergir entre as duas
 * telas. Transferir fornecedor, em particular, cria a categoria no destino e
 * clona o perfil fiscal: não é uma troca de campo.
 *
 * O hook cuida também do estado dos dois modais, já que abrir/fechar faz parte
 * do fluxo.
 */
export function useCategoryActions(supplierId: string) {
  const { activeCalcOption, versionInfo, activeBudgetId } = useBudgetMeta();
  const { supplierList, addedItemTypeList } = useBudgetSuppliersState();
  const { budgetServices } = useBudgetServicesState();
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();
  const { project } = useProject();
  const provider = useBudgetProvider(supplierId);

  const [moveModalType, setMoveModalType] = useState<BudgetItemType | null>(
    null
  );
  const [transferModalType, setTransferModalType] =
    useState<BudgetItemType | null>(null);

  const parsedSupplierId = useMemo(() => {
    if (!supplierId) return "";
    if (supplierId.includes("|")) return supplierId.split("|")[0];
    const parts = supplierId.split("-");
    return parts.length > 1 ? parts[0] : supplierId;
  }, [supplierId]);

  const confirmMoveCategory = useCallback(
    async (
      type: BudgetItemType,
      targetCalcOption: CalcOption
    ): Promise<boolean> => {
      const fromOption = activeCalcOption as CalcOption;
      if (fromOption === targetCalcOption) return false;

      const categoryLabel =
        BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum];

      // O modelo não suporta a mesma (fornecedor, categoria, forma de cobrança)
      // duas vezes: a de destino sobrescreveria a existente.
      const hasDuplicate = hasMoveCategoryDuplicate(
        { addedItemTypeList, budgetServices },
        { type, supplierId, toCalcOption: targetCalcOption },
        versionInfo
      );

      if (hasDuplicate) {
        toast.error(
          `Já existe a categoria ${categoryLabel} em ${targetCalcOption}`
        );
        return false;
      }

      activeBudgetDispatch({
        type: "MOVE_CATEGORY",
        payload: {
          type,
          typeName: categoryLabel,
          supplierId,
          fromCalcOption: fromOption,
          toCalcOption: targetCalcOption,
        },
        versionInfo,
      });

      if (!activeBudgetId) return true;

      // O dispatch só é aplicado no próximo commit; o autosave lê o orçamento
      // de `latestActiveBudgetRef`, que ainda não foi atualizado neste tick.
      return new Promise<boolean>((resolve) => {
        window.setTimeout(() => {
          void (async () => {
            const ok = await runBudgetAutosave("manual", {
              actionType: "MOVE_CATEGORY",
              auditContext: {
                "Categoria movida": categoryLabel,
                Fornecedor: provider?.name || supplierId,
                "Forma de cobrança": `${fromOption} → ${targetCalcOption}`,
              },
            });
            if (!ok) {
              toast.error(
                "Não foi possível salvar após mover a categoria. Verifique sua conexão e tente salvar de novo."
              );
            }
            resolve(ok);
          })();
        }, 0);
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
      addedItemTypeList,
      budgetServices,
      provider?.name,
    ]
  );

  /** Fornecedores que podem receber a categoria (ver `useTransferTargets`). */
  const transferTargets = useTransferTargets(activeCalcOption, supplierId);

  /** Aplica a transferência de categoria já confirmada no modal. */
  const confirmTransferCategory = useCallback(
    async (type: BudgetItemType, target: TransferTarget): Promise<boolean> => {
      const categoryLabel =
        BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum];

      // Um fornecedor novo que já ocupe um id nesta forma de cobrança precisa
      // de id próprio, senão as duas linhas disputam a mesma identidade.
      const toSupplierId = target.isNew
        ? resolveNewSupplierId(
            target.realSupplierId,
            activeCalcOption,
            supplierList,
            { isTemporary: target.isTemporary }
          )
        : target.existingSupplierId ?? target.realSupplierId;

      if (!toSupplierId || toSupplierId === supplierId) return false;

      const hasDuplicate = addedItemTypeList?.some(
        (el) =>
          el.type === type &&
          el.supplierId === toSupplierId &&
          el.calcOption === activeCalcOption
      );

      if (hasDuplicate) {
        toast.error(
          `O fornecedor de destino já possui a categoria ${categoryLabel} nesta forma de cobrança.`
        );
        return false;
      }

      const transferPayload: TransferCategorySupplierPayload = {
        type,
        typeName: categoryLabel,
        calcOption: activeCalcOption as CalcOption,
        fromSupplierId: supplierId,
        toSupplierId,
        toSupplierName: target.supplierName,
        createTargetSupplier: target.isNew,
      };

      const blockReason = findTransferCategoryBlockReason(
        { addedItemTypeList, budgetServices } as Budget,
        transferPayload,
        versionInfo
      );
      if (blockReason) {
        toast.error(blockReason);
        return false;
      }

      activeBudgetDispatch({
        type: "TRANSFER_CATEGORY_SUPPLIER",
        payload: transferPayload,
        versionInfo,
      });

      if (!activeBudgetId) return true;

      return new Promise<boolean>((resolve) => {
        window.setTimeout(() => {
          void (async () => {
            const ok = await runBudgetAutosave("manual", {
              actionType: "TRANSFER_CATEGORY",
              auditContext: {
                "Categoria transferida": categoryLabel,
                "Fornecedor de origem": provider?.name || supplierId,
                "Fornecedor de destino": target.supplierName,
                "Forma de cobrança": activeCalcOption,
              },
            });
            if (!ok) {
              toast.error(
                "Não foi possível salvar após transferir a categoria. Verifique sua conexão e tente salvar de novo."
              );
            }
            resolve(ok);
          })();
        }, 0);
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
      addedItemTypeList,
      budgetServices,
      supplierList,
      provider?.name,
    ]
  );

  const openMoveBillingModal = useCallback((type: BudgetItemType) => {
    setMoveModalType(type);
  }, []);

  const openTransferModal = useCallback((type: BudgetItemType) => {
    setTransferModalType(type);
  }, []);

  /** Exclui a categoria e tudo que depende dela, com confirmação. */
  const removeBudgetItem = useCallback(
    (type: BudgetItemType) => {
      Swal.fire({
        title: "Tem certeza?",
        text: "Ao apagar este item todos serviços relacionados também serão apagados!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Não, cancelar",
        confirmButtonText: "Sim, prossiga",
      }).then((result) => {
        if (!result.isConfirmed) return;
        // Só o reducer altera o orçamento; SAVE antes do recálculo persistia
        // `budgetValuesTotal` desatualizado (mesmo problema do DELETE_SERVICE).
        activeBudgetDispatch({
          type: "DELETE_BUDGET_ITEM",
          payload: { type, supplierId, calcOption: activeCalcOption },
          versionInfo,
        });
        if (activeBudgetId) {
          window.setTimeout(() => {
            void runBudgetAutosave("manual");
          }, 0);
        }
        Swal.fire({
          title: "Apagado",
          text: "O item foi apagado",
          icon: "success",
        });
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
    ]
  );

  return {
    moveModalType,
    setMoveModalType,
    transferModalType,
    setTransferModalType,
    transferTargets,
    parsedSupplierId,
    supplierLabel: provider?.name || "Fornecedor sem nome",
    confirmMoveCategory,
    confirmTransferCategory,
    openMoveBillingModal,
    openTransferModal,
    removeBudgetItem,
  };
}
